import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Stethoscope, RefreshCw, CheckCircle2, AlertTriangle, XCircle,
  Server, Cloud, Brain, CreditCard, Database, Bell, Send, Clock,
} from "lucide-react";
import { API } from "../../config";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const CATEGORY_META = {
  infra:   { icon: Server,     label: "Infrastruttura" },
  wallet:  { icon: CreditCard, label: "Digital Wallet" },
  crm:     { icon: Cloud,      label: "CRM" },
  ai:      { icon: Brain,      label: "AI Provider" },
  storage: { icon: Database,   label: "Storage" },
};

const STATUS_META = {
  ok:    { Icon: CheckCircle2,  cls: "text-emerald-300", bgCls: "bg-emerald-500/8 border-emerald-500/30",  label: "OK" },
  warn:  { Icon: AlertTriangle, cls: "text-amber-300",   bgCls: "bg-amber-500/8 border-amber-500/30",       label: "Warn" },
  error: { Icon: XCircle,       cls: "text-red-300",     bgCls: "bg-red-500/8 border-red-500/30",            label: "Error" },
};

export const SystemHealthCheckWidget = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [alertState, setAlertState] = useState(null);
  const [dispatching, setDispatching] = useState(false);
  const [dispatchMsg, setDispatchMsg] = useState(null);
  const [healing, setHealing] = useState(null); // "apple" | "google" | null

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [hc, last] = await Promise.all([
        axios.get(`${API}/admin/system/health-checks`),
        axios.get(`${API}/admin/system/health-checks/last-alert`),
      ]);
      setData(hc.data);
      setAlertState(last.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d?.msg || JSON.stringify(d)).join("; ")
        : (typeof detail === "string" ? detail : (e?.message || "Errore"));
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const dispatchAlert = useCallback(async () => {
    const confirmMsg = (
      "Stai per inviare un alert email a tutti i super-admin + IT (e Slack se configurato) " +
      "con lo stato corrente del System Health Check.\n\n" +
      "Confermi?"
    );
    if (!window.confirm(confirmMsg)) return;
    setDispatching(true);
    setDispatchMsg(null);
    try {
      const r = await axios.post(
        `${API}/admin/system/health-checks/dispatch-alert?force=true`
      );
      const d = r.data;
      const ch = d.channels || {};
      setDispatchMsg(
        `Alert inviato: email ${ch.email?.sent ? `✅ ${ch.email.recipients?.length || 0} destinatari` : "❌"} · ` +
        `Slack ${ch.slack?.sent ? "✅" : "—"} (${ch.slack?.reason || "n/d"})`
      );
      // Ricarica stato last-alert
      const last = await axios.get(`${API}/admin/system/health-checks/last-alert`);
      setAlertState(last.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d?.msg || JSON.stringify(d)).join("; ")
        : (typeof detail === "string" ? detail : (e?.message || "Errore"));
      setDispatchMsg(`Errore: ${msg}`);
    } finally {
      setDispatching(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  const overall = data?.overall_status;
  const overallMeta = overall ? STATUS_META[overall] : STATUS_META.ok;
  const OverallIcon = overallMeta.Icon;

  return (
    <div
      className="bg-[#0F0F0F] border border-gray-800 rounded-2xl p-6"
      data-testid="system-health-check-widget"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-emerald-300" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base">
              System Health Check
            </h3>
            <p className="text-gray-500 text-xs">
              Stato delle dipendenze esterne — verificare prima di un Save&apos;n&apos;Deploy
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {data && (
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${overallMeta.bgCls} ${overallMeta.cls}`}
              data-testid="shc-overall-badge"
            >
              <OverallIcon className="w-3.5 h-3.5" />
              {overallMeta.label.toUpperCase()}
            </span>
          )}
          <button
            type="button"
            onClick={fetchHealth}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#222] border border-gray-700 text-gray-200 px-3 py-1.5 rounded-lg text-xs disabled:opacity-50"
            data-testid="shc-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </button>
          <a
            href="/admin/system/history"
            className="inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#222] border border-gray-700 text-gray-200 px-3 py-1.5 rounded-lg text-xs"
            data-testid="shc-history-link"
            title="Cronologia dei cambi di stato — audit trail per post-mortem"
          >
            <Clock className="w-3.5 h-3.5" />
            Cronologia
          </a>
        </div>
      </div>

      {/* Summary counters */}
      {data && (
        <div className="grid grid-cols-4 gap-3 mb-5">
          <div className="bg-[#141414] border border-gray-800 rounded-lg p-3" data-testid="shc-summary-ok">
            <p className="text-[10px] uppercase tracking-wider text-gray-500">OK</p>
            <p className="text-2xl font-bold text-emerald-300">{data.summary.ok}</p>
          </div>
          <div className="bg-[#141414] border border-gray-800 rounded-lg p-3" data-testid="shc-summary-warn">
            <p className="text-[10px] uppercase tracking-wider text-gray-500">Warn</p>
            <p className="text-2xl font-bold text-amber-300">{data.summary.warn}</p>
          </div>
          <div className="bg-[#141414] border border-gray-800 rounded-lg p-3" data-testid="shc-summary-error">
            <p className="text-[10px] uppercase tracking-wider text-gray-500">Error</p>
            <p className="text-2xl font-bold text-red-300">{data.summary.error}</p>
          </div>
          <div className="bg-[#141414] border border-gray-800 rounded-lg p-3" data-testid="shc-summary-total">
            <p className="text-[10px] uppercase tracking-wider text-gray-500">Totale</p>
            <p className="text-2xl font-bold text-white">{data.summary.total}</p>
          </div>
        </div>
      )}

      {/* Alert configuration + manual dispatch */}
      {alertState && (
        <div
          className="mb-5 bg-[#141414] border border-gray-800 rounded-lg p-3 flex flex-wrap items-center gap-3"
          data-testid="shc-alert-config"
        >
          <Bell className="w-4 h-4 text-amber-300 flex-shrink-0" />
          <div className="flex-1 min-w-[200px] text-[12px] text-gray-400 leading-relaxed">
            <p>
              <span className="text-gray-300 font-semibold">Alert automatico:</span>{" "}
              email + Slack ogni mattina alle 09:00 CET (solo se warn/error).
              Slack:{" "}
              <span className={alertState.slack_configured ? "text-emerald-300" : "text-gray-500"}>
                {alertState.slack_configured ? "configurato ✅" : "non configurato (solo email)"}
              </span>
            </p>
            {alertState.last_alert_at && (
              <p className="text-[11px] text-gray-500 mt-0.5">
                Ultimo alert inviato: {fmtTime(alertState.last_alert_at)} · stato:{" "}
                <span className="font-mono">{alertState.last_overall_status}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={dispatchAlert}
            disabled={dispatching}
            className="inline-flex items-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/40 text-amber-300 px-3 py-1.5 rounded-lg text-xs disabled:opacity-50"
            data-testid="shc-dispatch-alert-btn"
          >
            {dispatching ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Invia alert ora
          </button>
        </div>
      )}

      {dispatchMsg && (
        <div
          className="mb-4 p-2 bg-[#141414] border border-gray-700 rounded-lg text-[11px] text-gray-300"
          data-testid="shc-dispatch-msg"
        >
          {dispatchMsg}
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          className="mb-4 flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl"
          data-testid="shc-error"
        >
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-300 text-xs leading-relaxed">{error}</p>
        </div>
      )}

      {/* Checks list */}
      {data?.checks?.length > 0 && (
        <div className="space-y-2" data-testid="shc-checks-list">
          {data.checks.map((c, idx) => {
            const statusMeta = STATUS_META[c.status] || STATUS_META.warn;
            const catMeta = CATEGORY_META[c.category] || { icon: Server, label: c.category };
            const CatIcon = catMeta.icon;
            const StatusIcon = statusMeta.Icon;
            return (
              <div
                key={idx}
                className={`flex items-start gap-3 p-3 rounded-lg border ${statusMeta.bgCls}`}
                data-testid={`shc-check-${c.category}-${idx}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <CatIcon className="w-4 h-4 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-white text-sm font-semibold">{c.name}</span>
                    <span className="text-[10px] uppercase tracking-wider text-gray-500">
                      {catMeta.label}
                    </span>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${statusMeta.cls} bg-[#0a0a0a]`}>
                      <StatusIcon className="w-2.5 h-2.5" />
                      {statusMeta.label}
                    </span>
                  </div>
                  <p className="text-gray-300 text-xs leading-relaxed">{c.message}</p>
                  {c.action_hint && (
                    <p className="text-[11px] text-gray-500 mt-1 italic leading-relaxed">
                      <span className="text-gray-400 font-semibold not-italic">Hint:</span> {c.action_hint}
                    </p>
                  )}
                  {/* Self-service heal buttons — Fabrizio CR 03/07/2026 */}
                  {c.status === "warn" && c.name === "APN Cert · Storage" && (
                    <button
                      type="button"
                      disabled={healing === "apple"}
                      onClick={async () => {
                        setHealing("apple");
                        try {
                          const r = await axios.post(`${API}/admin/system/wallet/apple/heal-from-disk`);
                          setDispatchMsg(`Apple wallet auto-heal: ${r.data.action} — ${r.data.reason || ""}`);
                          await fetchHealth();
                        } catch (e) {
                          setDispatchMsg(`Auto-heal fallito: ${e.response?.data?.detail || e.message}`);
                        } finally { setHealing(null); }
                      }}
                      className="mt-2 text-[11px] px-2.5 py-1 bg-lojack-blue-500/15 hover:bg-lojack-blue-500/25 border border-lojack-blue-500/40 text-lojack-blue-200 rounded-md disabled:opacity-60 inline-flex items-center gap-1.5"
                      data-testid="heal-apple-wallet-btn"
                    >
                      <RefreshCw className={`w-3 h-3 ${healing === "apple" ? "animate-spin" : ""}`} />
                      {healing === "apple" ? "Rimedio in corso…" : "Rimedia ora → migra a MongoDB"}
                    </button>
                  )}
                  {c.status === "warn" && c.name === "Google Wallet · Storage" && (
                    <button
                      type="button"
                      disabled={healing === "google"}
                      onClick={async () => {
                        setHealing("google");
                        try {
                          const r = await axios.post(`${API}/admin/system/wallet/google/heal-from-disk`);
                          setDispatchMsg(`Google wallet auto-heal: ${r.data.action} — ${r.data.reason || ""}`);
                          await fetchHealth();
                        } catch (e) {
                          setDispatchMsg(`Auto-heal fallito: ${e.response?.data?.detail || e.message}`);
                        } finally { setHealing(null); }
                      }}
                      className="mt-2 text-[11px] px-2.5 py-1 bg-lojack-blue-500/15 hover:bg-lojack-blue-500/25 border border-lojack-blue-500/40 text-lojack-blue-200 rounded-md disabled:opacity-60 inline-flex items-center gap-1.5"
                      data-testid="heal-google-wallet-btn"
                    >
                      <RefreshCw className={`w-3 h-3 ${healing === "google" ? "animate-spin" : ""}`} />
                      {healing === "google" ? "Rimedio in corso…" : "Rimedia ora → migra a MongoDB"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer ts */}
      {data?.ts && (
        <p className="text-[10px] text-gray-500 mt-4">
          Ultimo controllo: {fmtTime(data.ts)}
        </p>
      )}
    </div>
  );
};
